import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
// PrimeNG
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { CalendarModule } from 'primeng/calendar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DialogModule } from 'primeng/dialog';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

import { HeaderComponent } from './components/header/header.component';
import { BurgerComponent } from './components/icons/burger/burger.component';
import { PrimeSelectButtonComponent } from './components/prime/prime-select-button/prime-select-button.component';
import { FormsModule } from '@angular/forms';
import { PrimeCalendarComponent } from './components/prime/prime-calendar/prime-calendar.component';
import { PrimeDropdownComponent } from './components/prime/prime-dropdown/prime-dropdown.component';
import { PrimeMultiSelectComponent } from './components/prime/prime-multi-select/prime-multi-select.component';
import { PrimeNgCalendarMaskDirective } from './directives/prime-ng-calendar-mask.directive';
import { CommonDialogComponent } from './components/common-dialog/common-dialog.component';
import { DialogComponent } from './components/prime/dialog/dialog.component';
import { RegExInputDirective } from './directives/regex-input.directive';
import { DateTimePipe } from './pipes/date-time.pipe';
import { ValidationMessageComponent } from './components/validation-message/validation-message.component';
import { MathNotationPipe } from './pipes/math-notation.pipe';
import { FileSizePipe } from './pipes/file-size.pipe';
import { MskPipe } from './pipes/msk.pipe';
import { FormatDownloadUrlPipe } from './pipes/format-download-url.pipe';
import { AppFooterComponent } from './components/app-footer/app-footer.component';
import { FbLoaderSkeletonComponent } from './components/loaders/fb-loader-skeleton/fb-loader-skeleton.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { EnumTitlePipe } from './pipes/enum-title.pipe';
import { TrimFractionalPartPipe } from './pipes/trim-fractional-part.pipe';
import { DndDirective } from './dnd/dnd.directive';
import { FileDropComponent } from './dnd/file-drop/file-drop.component';
import { RouterModule } from '@angular/router';
import { ProlongOfferDialogComponent } from './components/prime/prolong-offer-dialog/prolong-offer-dialog.component';
import { FixedTopDirective } from './directives/fixed-top.directive';
import { TerminationPurchasesNumberPipe } from './pipes/termination-purchases-number.pipe';
import { SplitTableLinePipe } from './pipes/split-table-line.pipe';
import { FormatCharacteristicPipe } from './pipes/format-characteristic.pipe';
import { LoggerPipe } from './pipes/logger.pipe';
import { SortPipe } from './pipes/sort.pipe';
import { ToNumberPipe } from './pipes/to-number.pipe';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { NotesComponent } from './components/notes/notes.component';
import { HideSearchFilterDirective } from './directives/hide-search-filter.directive';
import { DigitsOnlyDirective } from './directives/digits-only.directive';
import { ShortenPipe } from './pipes/shorten.pipe';
import { M2DDocumentsOfferDialogComponent } from './components/m2d-documents-offer-dialog/m2d-documents-offer.component';
import { M2dDocumentsOfferItemDialogComponent } from './components/m2d-documents-offer-item-dialog/m2d-documents-offer-item-dialog.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DigitsTextOnlyDirective } from './directives/digits-text-only.directive';
import { FormattedThousandsPipe } from './pipes/formatted-thousands.pipe';
import { SimplePipeMskPipe } from './pipes/simple-pipe-msk.pipe';
import { UtcToMoscowPipe } from './pipes/utc-to-moscow.pipe';
import { ToggleTextDirective } from './directives/toggle-text.directive';
import { ToggleArrayDirective } from './directives/toggle-array.directive';
import { DigitsEngTextOnlyDirective } from './directives/digits-eng-text.directive';
import { CaptchaComponent } from './components/captcha/captcha.component';
import { ForbiddenModule } from './modules/forbidden/forbidden.module';
import { PermissionsDirective } from './directives/permissions.directive';
import { RegulationsWindowComponent } from './components/regulations-window/regulations-window.component';
import { DialogsModule } from './modules/dialog/dialog.module';
import { CustomerHeaderComponent } from './components/customer-header/customer-header.component';
import { LinkComponent } from './components/link/link.component';
import { EtpFeeNotPaidComponent } from './components/etp-fee-not-paid/etp-fee-not-paid.component';
import { DigitsMinMaxDirective } from './directives/digits-min-max.directive';
import { FloatMinMaxDirective } from './directives/float-digits-min-max.directive';

const declarations = [
  // pipes
  DateTimePipe,
  FileSizePipe,
  SplitTableLinePipe,
  MskPipe,
  SimplePipeMskPipe,
  ShortenPipe,
  FormatDownloadUrlPipe,
  EnumTitlePipe,
  TrimFractionalPartPipe,
  TerminationPurchasesNumberPipe,
  FormatCharacteristicPipe,
  SortPipe,
  ToNumberPipe,
  LoggerPipe,
  MathNotationPipe,
  UtcToMoscowPipe,
  // components
  HeaderComponent,
  CustomerHeaderComponent,
  BurgerComponent,
  PrimeSelectButtonComponent,
  PrimeCalendarComponent,
  PrimeDropdownComponent,
  PrimeMultiSelectComponent,
  CommonDialogComponent,
  DialogComponent,
  AppFooterComponent,
  FbLoaderSkeletonComponent,
  TooltipComponent,
  SpinnerComponent,
  NotesComponent,
  RegulationsWindowComponent,
  LinkComponent,
  EtpFeeNotPaidComponent,
  // directives
  PrimeNgCalendarMaskDirective,
  RegExInputDirective,
  FixedTopDirective,
  HideSearchFilterDirective,
  DigitsOnlyDirective,
  ToggleTextDirective,
  ToggleArrayDirective,
  PermissionsDirective,
  DigitsMinMaxDirective,
  FloatMinMaxDirective,
  // validation
  ValidationMessageComponent,
  // DnDInput
  DndDirective,
  FileDropComponent,
  ProlongOfferDialogComponent,
  M2DDocumentsOfferDialogComponent,
  M2dDocumentsOfferItemDialogComponent,
  DigitsTextOnlyDirective,
  FormattedThousandsPipe,
  DigitsEngTextOnlyDirective,
];

@NgModule({
  declarations,
  imports: [
    CommonModule,
    SelectButtonModule,
    FormsModule,
    CalendarModule,
    MultiSelectModule,
    DropdownModule,
    AccordionModule,
    DialogModule,
    ConfirmDialogModule,
    NgxSkeletonLoaderModule,
    RouterModule,
    AngularSvgIconModule,
    CaptchaComponent,
    DialogsModule,
    ForbiddenModule
  ],
  exports: declarations,
  providers: [ConfirmationService],
})
export class SharedModule {}
